/**
 * Setting up a scheduled withdrawal (SWP) or scheduled transfer (STP) for an
 * investor.
 *
 * One dialog for both because they are the same instruction with a different
 * destination: SWP pays out to the bank, STP buys another scheme in the same
 * AMC. Everything else — the eligible folio, the frequency, the instalment day,
 * the amount band, the count — is shared, and `Api/mfAdminPlans` is the shared
 * machine underneath.
 *
 * NEITHER TAKES A MANDATE OR A PAYMENT. These move units the investor already
 * holds, so there is no bank authorization leg and no popup: create, verify it
 * came back active, sync, done.
 *
 * The instalment day means different things at different frequencies — a
 * weekday for a weekly plan, a day of the month otherwise — so the picker reads
 * `Helpers/mfSchedule` rather than assuming.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  canCancelMFPlan,
  cancelMFPlan,
  checkMFPlanFolios,
  createMFPlan,
  fetchMFPlans,
  getMFPlanError,
  getMFPlanFrequencyRules,
  getMFPlanId,
  toMFPlanSource,
  updateMFPlanAmount,
} from "../../Api/mfAdminPlans";
import type {
  MFFolioEligibility,
  MFPlanFrequencyRule,
  MFPlanKind,
  MFPlanSource,
  MFScheduledPlan,
} from "../../Api/mfAdminPlans";
import {
  fetchMFSchemesByFundName,
  getMFErrorMessage,
} from "../../Api/mfAdminInvestments";
import type { MFScheme } from "../../Api/mfAdminInvestments";
import {
  formatMFInstallmentDay,
  getAllowedMFInstallmentDays,
  getDefaultMFInstallmentDay,
  mfFrequencyNeedsInstallmentDay,
} from "../../Helpers/mfSchedule";
import styles from "../../pages/MutualFunds/user-investments-dashboard.module.css";

const NOUN: Record<MFPlanKind, { title: string; short: string; verb: string }> = {
  swp: { title: "Set up a withdrawal plan", short: "SWP", verb: "withdraw" },
  stp: { title: "Set up a transfer plan", short: "STP", verb: "transfer" },
};

const currency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const isAmountMultiple = (amount: number, multiple: number) => {
  if (!Number.isFinite(amount) || !Number.isFinite(multiple) || multiple <= 0) {
    return false;
  }
  const quotient = amount / multiple;
  return Math.abs(quotient - Math.round(quotient)) < 1e-8;
};

const InlineLoader = () => <span className={styles.inlineSpinner} aria-hidden="true" />;

export interface ScheduledPlanDialogProps {
  open: boolean;
  kind: MFPlanKind;
  userUuid: string;
  /** Folio numbers from the investor's holdings, to check eligibility against. */
  folioNumbers: string[];
  /** Fund name per folio, for labelling. */
  fundNameByFolio: Record<string, string>;
  /** AMC name per folio — an STP can only move within the same AMC. */
  amcByFolio: Record<string, string>;
  onClose: () => void;
  onComplete: () => void;
}

const ScheduledPlanDialog: React.FC<ScheduledPlanDialogProps> = ({
  open,
  kind,
  userUuid,
  folioNumbers,
  fundNameByFolio,
  amcByFolio,
  onClose,
  onComplete,
}) => {
  const noun = NOUN[kind];

  const [eligibility, setEligibility] = useState<MFFolioEligibility[]>([]);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState("");

  const [plans, setPlans] = useState<MFScheduledPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const [folio, setFolio] = useState("");
  const [frequency, setFrequency] = useState("");
  const [day, setDay] = useState(0);
  const [amount, setAmount] = useState("");
  const [installments, setInstallments] = useState(12);

  const [destinations, setDestinations] = useState<MFScheme[]>([]);
  const [destinationsLoading, setDestinationsLoading] = useState(false);
  const [destination, setDestination] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [busyPlanId, setBusyPlanId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ── which folios can carry a plan ── */
  const loadEligibility = useCallback(async () => {
    setEligibilityLoading(true);
    setEligibilityError("");
    try {
      setEligibility(await checkMFPlanFolios(folioNumbers));
    } catch (requestError) {
      setEligibility([]);
      setEligibilityError(
        getMFPlanError(requestError, "Folio eligibility could not be checked.")
      );
    } finally {
      setEligibilityLoading(false);
    }
  }, [folioNumbers]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      setPlans(await fetchMFPlans(kind, userUuid));
    } catch (requestError) {
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [kind, userUuid]);

  useEffect(() => {
    if (!open) return;
    setFolio("");
    setFrequency("");
    setAmount("");
    setDestination("");
    setError("");
    setSuccess("");
    loadEligibility();
    loadPlans();
  }, [open, loadEligibility, loadPlans]);

  const selected = useMemo(
    () => eligibility.find((row) => row.folio_number === folio) || null,
    [eligibility, folio]
  );

  const source: MFPlanSource | null = useMemo(() => {
    if (!selected) return null;
    return toMFPlanSource(
      selected,
      fundNameByFolio[selected.folio_number] || selected.scheme
    );
  }, [selected, fundNameByFolio]);

  const frequencyRules: MFPlanFrequencyRule[] = useMemo(
    () => (selected ? getMFPlanFrequencyRules(kind, selected.scheme_details) : []),
    [kind, selected]
  );

  const rule = useMemo(
    () => frequencyRules.find((row) => row.frequency === frequency) || null,
    [frequencyRules, frequency]
  );

  const allowedDays = useMemo(
    () => (rule ? getAllowedMFInstallmentDays(rule.frequency, rule.dates) : []),
    [rule]
  );

  /* Frequency and day follow the folio; both come from the scheme, not a guess. */
  useEffect(() => {
    if (!frequencyRules.length) {
      setFrequency("");
      return;
    }
    const preferred =
      frequencyRules.find((row) => row.frequency === "monthly") || frequencyRules[0];
    setFrequency(preferred.frequency);
  }, [frequencyRules]);

  useEffect(() => {
    if (!rule) {
      setDay(0);
      return;
    }
    setDay(getDefaultMFInstallmentDay(rule.frequency, rule.dates) || 0);
    setInstallments((current) =>
      Math.max(current, Math.ceil(rule.minInstallments || 1))
    );
    if (rule.minAmount) setAmount((current) => current || String(rule.minAmount));
  }, [rule]);

  /* ── STP destinations: same AMC only ── */
  useEffect(() => {
    if (kind !== "stp" || !selected) {
      setDestinations([]);
      return;
    }
    const amc = amcByFolio[selected.folio_number] || "";
    if (!amc) {
      setDestinations([]);
      return;
    }
    let active = true;
    setDestinationsLoading(true);
    fetchMFSchemesByFundName(amc)
      .then((schemes) => {
        if (!active) return;
        setDestinations(
          schemes.filter(
            (scheme) =>
              scheme.switch_in_allowed === true &&
              scheme.active !== false &&
              !scheme.merged &&
              scheme.isin !== selected.scheme
          )
        );
      })
      .catch((requestError) => {
        if (!active) return;
        setDestinations([]);
        setError(getMFErrorMessage(requestError, "Destination schemes could not be loaded."));
      })
      .then(() => {
        if (active) setDestinationsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [kind, selected, amcByFolio]);

  const amountNumber = Number(amount);
  const needsDay = frequency ? mfFrequencyNeedsInstallmentDay(frequency) : false;
  const minInstallments = Math.max(1, Math.ceil(rule?.minInstallments || 1));

  const problems: string[] = [];
  if (!source) {
    problems.push(
      selected
        ? "This folio has no purchase or SIP reference to schedule against."
        : "Choose a folio."
    );
  }
  if (!rule) problems.push("Choose a frequency this scheme supports.");
  if (kind === "stp" && !destination) problems.push("Choose a destination scheme.");
  if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
    problems.push("Enter an instalment amount.");
  } else if (rule) {
    if (rule.minAmount && amountNumber < rule.minAmount) {
      problems.push(`The minimum instalment is ${currency(rule.minAmount)}.`);
    }
    if (rule.maxAmount && amountNumber > rule.maxAmount) {
      problems.push(`The maximum instalment is ${currency(rule.maxAmount)}.`);
    }
    if (rule.amountMultiple && !isAmountMultiple(amountNumber, rule.amountMultiple)) {
      problems.push(`The amount must be in multiples of ${currency(rule.amountMultiple)}.`);
    }
  }
  if (!Number.isInteger(installments) || installments < minInstallments) {
    problems.push(`At least ${minInstallments} instalments are required.`);
  }
  if (needsDay && allowedDays.indexOf(day) < 0) {
    problems.push("Choose an instalment day this frequency allows.");
  }

  const canSubmit = !problems.length && !submitting && !eligibilityLoading;

  const submit = async () => {
    if (!canSubmit || !source || !rule) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const plan = await createMFPlan(kind, {
        source,
        amount: amountNumber,
        frequency: rule.frequency,
        numberOfInstallments: installments,
        installmentDay: needsDay ? day : undefined,
        destinationIsin: kind === "stp" ? destination : undefined,
      });
      setSuccess(
        `${noun.short} ${getMFPlanId(kind, plan)} is active on folio ${source.folioNumber}.`
      );
      setAmount("");
      await loadPlans();
      onComplete();
    } catch (requestError) {
      setError(getMFPlanError(requestError, `The ${noun.short} could not be created.`));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPlan = async (plan: MFScheduledPlan) => {
    const planId = getMFPlanId(kind, plan);
    setBusyPlanId(planId);
    setError("");
    try {
      await cancelMFPlan(kind, plan);
      setSuccess(`${noun.short} ${planId} was cancelled.`);
      await loadPlans();
      onComplete();
    } catch (requestError) {
      setError(getMFPlanError(requestError, `The ${noun.short} could not be cancelled.`));
    } finally {
      setBusyPlanId("");
    }
  };

  const changeAmount = async (plan: MFScheduledPlan) => {
    const planId = getMFPlanId(kind, plan);
    const next = window.prompt(
      `New instalment amount for ${noun.short} ${planId}`,
      String(plan.amount || "")
    );
    if (next == null) return;
    const value = Number(next);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    setBusyPlanId(planId);
    setError("");
    try {
      await updateMFPlanAmount(kind, plan, value);
      setSuccess(`${noun.short} ${planId} now withdraws ${currency(value)}.`);
      await loadPlans();
      onComplete();
    } catch (requestError) {
      setError(getMFPlanError(requestError, `The ${noun.short} could not be updated.`));
    } finally {
      setBusyPlanId("");
    }
  };

  const eligibleFolios = eligibility.filter((row) => toMFPlanSource(row, "") != null);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <div className={styles.dialogTitle}>
          <div>
            <h2>{noun.title}</h2>
            <p>
              Admin instruction for this investor · no consent OTP · no mandate
              needed
            </p>
          </div>
          <IconButton size="small" onClick={submitting ? undefined : onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={styles.form}>
          <div className={styles.info}>
            {kind === "swp"
              ? "A withdrawal plan redeems a fixed amount from one folio on a schedule and credits the investor's bank."
              : "A transfer plan moves a fixed amount from one folio into another scheme in the same AMC on a schedule."}
          </div>

          {eligibilityLoading && (
            <div className={styles.info}>
              <InlineLoader /> Checking which folios can carry a {noun.short}…
            </div>
          )}
          {eligibilityError && <div className={styles.error}>{eligibilityError}</div>}

          <div className={styles.field}>
            <label>Source folio</label>
            <select
              className={styles.select}
              value={folio}
              disabled={eligibilityLoading || submitting}
              onChange={(event) => {
                setFolio(event.target.value);
                setError("");
                setSuccess("");
              }}
            >
              <option value="">Select a folio</option>
              {eligibleFolios.map((row) => (
                <option key={row.folio_number} value={row.folio_number}>
                  Folio {row.folio_number} ·{" "}
                  {fundNameByFolio[row.folio_number] || row.scheme}
                </option>
              ))}
            </select>
            {!eligibilityLoading && !eligibleFolios.length && (
              <div className={styles.helper}>
                None of this investor&apos;s folios came back eligible for a{" "}
                {noun.short}.
              </div>
            )}
          </div>

          {kind === "stp" && selected && (
            <div className={styles.field}>
              <label>Destination scheme · same AMC</label>
              <select
                className={styles.select}
                value={destination}
                disabled={destinationsLoading || submitting}
                onChange={(event) => setDestination(event.target.value)}
              >
                <option value="">Select destination</option>
                {destinations.map((scheme) => (
                  <option key={scheme.isin} value={scheme.isin}>
                    {scheme.name}
                  </option>
                ))}
              </select>
              {destinationsLoading && (
                <div className={styles.helper}>
                  <InlineLoader /> Loading switch-in eligible schemes…
                </div>
              )}
              {!destinationsLoading && !destinations.length && (
                <div className={styles.helper}>
                  No switch-in eligible scheme was returned for this AMC.
                </div>
              )}
            </div>
          )}

          {selected && (
            <>
              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label>Frequency</label>
                  <select
                    className={styles.select}
                    value={frequency}
                    disabled={submitting}
                    onChange={(event) => setFrequency(event.target.value)}
                  >
                    {frequencyRules.map((row) => (
                      <option key={row.frequency} value={row.frequency}>
                        {row.frequency.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                  {!frequencyRules.length && (
                    <div className={styles.helper}>
                      This scheme publishes no {noun.short} frequency.
                    </div>
                  )}
                </div>
                <div className={styles.field}>
                  <label>{needsDay ? "Instalment day" : "Schedule"}</label>
                  {needsDay ? (
                    <select
                      className={styles.select}
                      value={day}
                      disabled={submitting}
                      onChange={(event) => setDay(Number(event.target.value))}
                    >
                      {allowedDays.map((value) => (
                        <option key={value} value={value}>
                          {formatMFInstallmentDay(frequency, value)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className={styles.helper} style={{ marginTop: 11 }}>
                      This frequency runs every business day — no instalment day
                      is sent.
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label>Instalment amount</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={amount}
                    disabled={submitting}
                    min={rule?.minAmount || undefined}
                    max={rule?.maxAmount || undefined}
                    step={rule?.amountMultiple || undefined}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                  {rule && (
                    <div className={styles.helper}>
                      {rule.minAmount ? `Minimum ${currency(rule.minAmount)}` : "No minimum published"}
                      {rule.maxAmount ? ` · Maximum ${currency(rule.maxAmount)}` : ""}
                      {rule.amountMultiple
                        ? ` · Multiples of ${currency(rule.amountMultiple)}`
                        : ""}
                    </div>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Number of instalments</label>
                  <input
                    className={styles.input}
                    type="number"
                    min={minInstallments}
                    value={installments}
                    disabled={submitting}
                    onChange={(event) => setInstallments(Number(event.target.value))}
                  />
                  <div className={styles.helper}>At least {minInstallments}.</div>
                </div>
              </div>
            </>
          )}

          {problems.length > 0 && folio ? (
            <div className={styles.warning}>{problems[0]}</div>
          ) : null}
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.info}>{success}</div>}

          <div className={styles.dialogActions}>
            <button className={styles.dialogCancel} onClick={onClose} disabled={submitting}>
              Close
            </button>
            <button
              className={styles.dialogSubmit}
              disabled={!canSubmit}
              aria-busy={submitting || undefined}
              onClick={submit}
            >
              {submitting && <InlineLoader />}{" "}
              {submitting ? `Creating ${noun.short}…` : `Create ${noun.short}`}
            </button>
          </div>

          {/* ── what already runs on this investor ── */}
          <div className={styles.sectionHeader} style={{ marginTop: 6 }}>
            <div>
              <h2 style={{ fontSize: 15 }}>Existing {noun.short} plans</h2>
              <p>Amount can be changed; the plan itself can be cancelled.</p>
            </div>
          </div>
          {plansLoading ? (
            <div className={styles.info}>
              <InlineLoader /> Loading {noun.short} plans…
            </div>
          ) : plans.length ? (
            <div className={styles.tableWrap}>
              <table className={styles.table} style={{ minWidth: 520 }}>
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Amount</th>
                    <th>Schedule</th>
                    <th>Next</th>
                    <th>State</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => {
                    const planId = getMFPlanId(kind, plan);
                    const busy = busyPlanId === planId;
                    return (
                      <tr key={planId}>
                        <td>{plan.folio_number || "—"}</td>
                        <td>
                          <b>{currency(Number(plan.amount || 0))}</b>
                        </td>
                        <td>
                          {String(plan.frequency || "—").replace(/_/g, " ")}
                          {plan.installment_day
                            ? ` · ${formatMFInstallmentDay(
                                String(plan.frequency || ""),
                                Number(plan.installment_day)
                              )}`
                            : ""}
                        </td>
                        <td>{formatDate(plan.next_installment_date)}</td>
                        <td>{String(plan.state || plan.webhook_status || "—")}</td>
                        <td>
                          <div className={styles.rowActions}>
                            {canCancelMFPlan(plan) && (
                              <>
                                <button
                                  className={styles.miniButton}
                                  disabled={Boolean(busyPlanId)}
                                  onClick={() => changeAmount(plan)}
                                >
                                  {busy && <InlineLoader />} Amount
                                </button>
                                <button
                                  className={`${styles.miniButton} ${styles.miniDanger}`}
                                  disabled={Boolean(busyPlanId)}
                                  onClick={() => cancelPlan(plan)}
                                >
                                  Cancel
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.helper}>
              No {noun.short} plan exists for this investor yet.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduledPlanDialog;
